import api from '../utils/api';
import toast from 'react-hot-toast';

// Common bill generation and print functions
export const handlePrintBill = async (checkinId) => {
  try {
    // Fetch bill data from dedicated API
    const response = await api.get(`/vendor/checkin-management/bill/${checkinId}`);
    const { bill } = response.data;

    // Create print window
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    // Generate bill HTML
    const billHTML = generateBillHTML(bill);
    
    // Write to print window
    printWindow.document.write(billHTML);
    printWindow.document.close();
    
    // Wait for content to load then print
    printWindow.onload = () => {
      printWindow.print();
      printWindow.onafterprint = () => {
        printWindow.close();
      };
    };
  } catch (error) {
    console.error('Error printing bill:', error);
    toast.error(error.response?.data?.message || 'Failed to generate bill');
  }
};

export const generateBillHTML = (bill) => {
  // Extract data from bill object
  const vendor = bill.vendor;
  const guest = bill.guest;
  const booking = bill.booking;
  const persons = bill.persons;
  const charges = bill.charges;
  const payments = bill.payments;

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Bill - ${bill.billNumber}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: Arial, sans-serif; padding: 20px; }
        .bill-container { max-width: 800px; margin: 0 auto; }
        .header { text-center; border-bottom: 2px solid #333; padding-bottom: 15px; margin-bottom: 20px; }
        .header h1 { font-size: 28px; margin-bottom: 10px; }
        .header p { color: #666; margin: 5px 0; }
        .bill-title { text-center; margin: 20px 0; }
        .bill-title h2 { font-size: 24px; }
        .bill-title p { color: #666; margin-top: 5px; }
        .details-section { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 20px; border-bottom: 1px solid #ddd; padding-bottom: 20px; }
        .detail-group h3 { font-size: 16px; margin-bottom: 10px; }
        .detail-group p { margin: 5px 0; color: #333; }
        .persons-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
        .persons-table th, .persons-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        .persons-table th { background-color: #f5f5f5; font-weight: bold; }
        .bill-summary { margin: 20px 0; }
        .bill-summary h3 { font-size: 18px; margin-bottom: 15px; }
        .bill-summary table { width: 100%; }
        .bill-summary tr { border-bottom: 1px solid #ddd; }
        .bill-summary td { padding: 10px 0; }
        .bill-summary .total-row { font-weight: bold; font-size: 18px; border-top: 2px solid #333; }
        .bill-summary .paid-row { color: green; }
        .bill-summary .discount-row { color: red; }
        .footer { margin-top: 30px; padding-top: 20px; border-top: 2px solid #333; }
        .footer .thank-you { text-align: center; margin-bottom: 20px; }
        .footer .signature { text-align: right; margin-top: 30px; }
        .footer .signature-line { border-top: 1px solid #666; width: 200px; margin-left: auto; margin-top: 50px; }
        .print-date { text-align: center; color: #999; font-size: 12px; margin-top: 20px; }
        @media print {
          body { padding: 10px; margin: 0; font-size: 12px; }
          .bill-container { max-width: 100%; margin: 0; }
          .header h1 { font-size: 20px; }
          .header p { font-size: 10px; }
          .bill-title h2 { font-size: 18px; }
          .detail-group h3 { font-size: 12px; }
          .detail-group p { font-size: 10px; margin: 3px 0; }
          .bill-summary h3 { font-size: 14px; }
          .bill-summary td { font-size: 10px; padding: 5px 0; }
          .persons-table th, .persons-table td { font-size: 9px; padding: 4px; }
          .footer .thank-you p { font-size: 10px; }
          .details-section { gap: 20px; margin-bottom: 15px; }
          .bill-summary { margin: 15px 0; }
          .footer { margin-top: 20px; padding-top: 15px; }
          @page { margin: 10mm; size: A4; }
        }
      </style>
    </head>
    <body>
      <div class="bill-container">
        <div class="header" style="text-align: center;">
          <h1>${vendor?.businessName || 'Hotel Name'}</h1>
          <p>${vendor?.address || 'Hotel Address'}</p>
          <p>Phone: ${vendor?.contactNumber || 'N/A'} | Email: ${vendor?.email || 'N/A'}</p>
          ${vendor?.gstNumber ? `<p>GST No: ${vendor.gstNumber}</p>` : ''}
        </div>

        <div class="bill-title">
          <h2>GUEST BILL</h2>
          <p>Bill No: ${bill.billNumber}</p>
        </div>

        <div class="details-section">
          <div class="detail-group">
            <h3>Guest Details:</h3>
            <p>Name: ${guest?.name || 'N/A'}</p>
            <p>Mobile: ${guest?.mobileNumber || 'N/A'}</p>
            <p>ID Number: ${guest?.identityNumber || 'N/A'}</p>
            <p>GST Number: ${booking?.customerGstNumber || 'N/A'}</p>
          </div>
          <div class="detail-group">
            <h3>Booking Details:</h3>
            <p>Room: ${booking?.roomNumber || 'N/A'}</p>
            <p>Check-in: ${booking?.checkinDateTime ? new Date(booking.checkinDateTime).toLocaleString('en-IN') : 'N/A'}</p>
            <p>Check-out: ${booking?.checkoutDateTime ? new Date(booking.checkoutDateTime).toLocaleString('en-IN') : 'N/A'}</p>
            <p>Payment Method: ${booking?.paymentMethod || 'Cash'}</p>
          </div>
        </div>

        ${persons && persons.length > 0 ? `
          <div style="margin-bottom: 20px;">
            <h3 style="margin-bottom: 10px;">Guest Details:</h3>
            <table class="persons-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Age</th>
                  <th>Gender</th>
                  <th>ID Number</th>
                </tr>
              </thead>
              <tbody>
                ${persons.map(person => `
                  <tr>
                    <td>${person.name || 'N/A'}</td>
                    <td>${person.age || 'N/A'}</td>
                    <td>${person.gender || 'N/A'}</td>
                    <td>${person.identityNumber || 'N/A'}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        ` : ''}

        <div class="bill-summary">
          <h3>Bill Summary:</h3>
          <table>
            <tr>
              <td>Room Charges (Base Amount)</td>
              <td style="text-align: right;">${charges?.baseAmount?.toFixed(2) || '0.00'}</td>
            </tr>
            <tr>
              <td>GST (${charges?.gstPercentage || 0}%)</td>
              <td style="text-align: right;">${charges?.gstAmount?.toFixed(2) || '0.00'}</td>
            </tr>
            ${charges?.otherAmount > 0 ? `
              <tr>
                <td>Other Charges ${charges?.otherAmountRemark ? `<br><small style="color: #666;">(${charges.otherAmountRemark})</small>` : ''}</td>
                <td style="text-align: right;">${charges.otherAmount.toFixed(2)}</td>
              </tr>
            ` : ''}
            <tr class="total-row">
              <td>Total Amount</td>
              <td style="text-align: right;">${charges?.totalAmount?.toFixed(2) || '0.00'}</td>
            </tr>
            <tr class="paid-row">
              <td>Advance Paid</td>
              <td style="text-align: right;">${payments?.advancePaid?.toFixed(2) || '0.00'}</td>
            </tr>
            <tr class="paid-row">
              <td>Amount Settled at Checkout</td>
              <td style="text-align: right;">${payments?.settledAmount?.toFixed(2) || '0.00'}</td>
            </tr>
            ${payments?.tipAmount > 0 ? `
              <tr class="paid-row">
                <td>Tip Amount</td>
                <td style="text-align: right;">${payments.tipAmount.toFixed(2)}</td>
              </tr>
            ` : ''}
            ${payments?.discountAmount > 0 ? `
              <tr class="discount-row">
                <td>Discount</td>
                <td style="text-align: right;">-${payments.discountAmount.toFixed(2)}</td>
              </tr>
            ` : ''}
          </table>
        </div>

        ${booking?.specialRequests ? `
          <div style="margin: 20px 0; padding-top: 15px; border-top: 1px solid #ddd;">
            <h3 style="margin-bottom: 10px;">Special Requests:</h3>
            <p>${booking.specialRequests}</p>
          </div>
        ` : ''}

        ${booking?.notes ? `
          <div style="margin: 20px 0;">
            <h3 style="margin-bottom: 10px;">Notes:</h3>
            <p>${booking.notes}</p>
          </div>
        ` : ''}

        <div class="footer">
          <div class="thank-you">
            <p style="margin-bottom: 10px;">Thank you for staying with us!</p>
            <p style="font-size: 14px;">We hope to see you again soon.</p>
          </div>
          <div class="signature">
            <p style="font-weight: bold;">Authorized Signature</p>
            <div class="signature-line"></div>
          </div>
        </div>

        <div class="print-date">
          <p>Printed on: ${new Date().toLocaleString('en-IN')}</p>
        </div>
      </div>
    </body>
    </html>
  `;
};
